import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/api/axios";
import { useAuthStore } from "@/store/auth";
import { useToastHandler } from "@/hooks/useToastHandler";
import { WorkoutModelJsonResource } from "@/types/workoutTypes";
import { SetModelJsonResource } from "@/types/setTypes";

const ERROR_MSG = "Failed to fetch recent sets";
const API_PATH = "/api/workouts";
const USE_QUERY_KEY = API_PATH;

// Use a type alias for better readability
type WorkoutsResponse = {
  workouts: WorkoutModelJsonResource[];
  sets: SetModelJsonResource[];
  active_dates: {
    later_dates: string[];
    current: string;
    earlier_dates: string[];
  };
};

// Hook for fetching recent sets using useQuery, structured like useUserInfo
export function useMyWorkouts(date?: string) {
  const { token } = useAuthStore();
  const { handleError } = useToastHandler();

  // if date is undefined, set as today:
  if (date === undefined) {
    date = new Date().toISOString().split("T")[0];
  }

  return useQuery<WorkoutsResponse>({
    enabled: !!token,
    queryKey: [USE_QUERY_KEY, date],
    //refetchInterval: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const url = date ? `${API_PATH}/${date}` : API_PATH;
      try {
        const res = await api.get(url);

        if (import.meta.env.DEV) {
          console.log(
            `>>> React Hook Fetching .... [${USE_QUERY_KEY}, ${url}]`,
            res.data.data
          );
        }

        // Check if the response is already in the correct format
        if (res.data && typeof res.data === "object" && "data" in res.data) {
          return res.data.data as WorkoutsResponse;
        }

        throw new Error(ERROR_MSG + "(Invalid Data)");
      } catch (err: unknown) {
        handleError(
          err instanceof Error ? err.message : ERROR_MSG,
          3000,
          err instanceof Error ? err.stack ?? "" : ""
        );
        console.log("...>>> err", err);
        //You should throw the error in your query function, not `return null`.
        // This way, React Query will set isError: true and your error UI will be shown.
        throw err;
      }
    },
  });
}

// invalidate cache:
export function useInvalidateMyWorkouts() {
  const queryClient = useQueryClient();
  return (date?: string) => {
    queryClient.invalidateQueries({ queryKey: [USE_QUERY_KEY, date] });
  };
}

// refetch cache:
export function useRefetchMyWorkouts() {
  const { refetch } = useMyWorkouts();
  return refetch;
}
